import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Checkbox, FormControlLabel,
  Alert, IconButton, Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

/**
 * PACTCredentialDialog — One-time display of OAuth2 credentials from PACT.
 * After accepting an invitation or rotating credentials, PACT returns
 * client_id and client_secret in plaintext. This dialog shows them once
 * with copy-to-clipboard and requires the user to confirm they've saved them.
 */
export default function PACTCredentialDialog({ open, onClose, credentials }) {
  const [confirmed, setConfirmed] = useState(false);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).catch(() => {
      // Fallback for non-HTTPS contexts
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
  };

  const handleClose = () => {
    setConfirmed(false);
    onClose();
  };

  if (!credentials) return null;

  return (
    <Dialog open={open} onClose={confirmed ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>PACT OAuth2 Credentials</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This client secret will not be shown again. Copy it now and store it securely.
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Client ID</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              value={credentials.clientId || ''}
              fullWidth
              size="small"
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
            <Tooltip title="Copy">
              <IconButton size="small" onClick={() => handleCopy(credentials.clientId)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Client Secret</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              value={credentials.clientSecret || ''}
              fullWidth
              size="small"
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
            <Tooltip title="Copy">
              <IconButton size="small" onClick={() => handleCopy(credentials.clientSecret)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {credentials.pact_connection_id && (
          <Typography variant="body2" color="text.secondary">
            PACT Connection ID: {credentials.pact_connection_id}
          </Typography>
        )}

        <FormControlLabel
          control={
            <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          }
          label="I have saved these credentials securely"
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={!confirmed} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
